// src/services/api/pushNotifications.ts
// 推播通知 API 服務層

import { functions } from '../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import { logInfo, logError } from '../../utils/logger';

/**
 * 推播通知內容
 */
export interface PushNotification {
  title: string;
  body: string;
  icon?: string;
}

/**
 * 推播自訂數據
 */
export interface PushData {
  clickAction?: string;
  type?: string;
  [key: string]: any;
}

/**
 * 推播發送結果
 */
export interface PushResult {
  success: boolean;
  message?: string;
  results?: {
    successCount: number;
    failureCount: number;
    totalTokens: number;
  };
  error?: string;
}

/**
 * 發送測試推播通知
 *
 * 用途：測試推播系統是否正常運作
 * 會發送一則預設的測試通知給當前用戶
 *
 * @returns Promise<PushResult>
 */
export const sendTestPushNotification = async (): Promise<PushResult> => {
  try {
    logInfo('PushNotificationAPI', '發送測試推播通知');

    const sendTest = httpsCallable<void, PushResult>(
      functions,
      'sendTestPushNotification'
    );

    const result = await sendTest();

    logInfo('PushNotificationAPI', '測試推播發送成功', result.data);
    return result.data;
  } catch (error: any) {
    logError('PushNotificationAPI', '測試推播發送失敗', error);
    return {
      success: false,
      error: error.message || '測試推播發送失敗',
    };
  }
};

/**
 * 發送自訂推播通知
 *
 * 用途：發送自訂內容的推播給當前用戶
 *
 * @param notification - 通知內容
 * @param customData - 自訂數據（可選）
 * @param priority - 優先級 'high' | 'normal'（預設 'normal'）
 * @returns Promise<PushResult>
 *
 * @example
 * ```ts
 * const result = await sendCustomPushNotification(
 *   {
 *     title: '任務提醒',
 *     body: '你有 3 個任務即將到期'
 *   },
 *   {
 *     clickAction: '/tasks',
 *     type: 'task_reminder'
 *   },
 *   'high'
 * );
 * ```
 */
export const sendCustomPushNotification = async (
  notification: PushNotification,
  customData?: PushData,
  priority: 'high' | 'normal' = 'normal'
): Promise<PushResult> => {
  try {
    logInfo('PushNotificationAPI', '發送自訂推播通知', { notification, customData });

    const sendCustom = httpsCallable<
      {
        notification: PushNotification;
        customData?: PushData;
        priority: 'high' | 'normal';
      },
      PushResult
    >(functions, 'sendCustomPushNotification');

    const result = await sendCustom({
      notification,
      customData,
      priority,
    });

    logInfo('PushNotificationAPI', '自訂推播發送成功', result.data);
    return result.data;
  } catch (error: any) {
    logError('PushNotificationAPI', '自訂推播發送失敗', error);
    return {
      success: false,
      error: error.message || '推播發送失敗',
    };
  }
};

/**
 * 發送測試推播給指定用戶（僅開發環境）
 *
 * ⚠️ 此功能僅在開發環境可用
 * 生產環境會直接拒絕
 *
 * @param targetUserId - 目標用戶 ID
 * @param message - 自訂訊息（可選）
 * @returns Promise<PushResult>
 */
export const sendTestPushToUser = async (
  targetUserId: string,
  message?: string
): Promise<PushResult> => {
  try {
    logInfo('PushNotificationAPI', '發送測試推播給指定用戶', { targetUserId });

    const sendToUser = httpsCallable<
      {
        targetUserId: string;
        message?: string;
      },
      PushResult
    >(functions, 'sendTestPushToUser');

    const result = await sendToUser({
      targetUserId,
      message,
    });

    logInfo('PushNotificationAPI', '測試推播發送成功', result.data);
    return result.data;
  } catch (error: any) {
    logError('PushNotificationAPI', '測試推播發送失敗', error);
    return {
      success: false,
      error: error.message || '測試推播發送失敗',
    };
  }
};
